import type { IsoTimestamp } from './common.js';

// Account page "API Connections" section — the live Zernio connection state
// for an account's profile, served by GET /api/accounts/:id/api-connections.

// The six platform rows, in display order. Values are Zernio SocialAccount
// `platform` identifiers.
export const API_CONNECTION_PLATFORMS = [
  'metaads',
  'facebook',
  'instagram',
  'googlebusiness',
  'googleads',
  'tiktok',
] as const;

export type ApiConnectionPlatform = (typeof API_CONNECTION_PLATFORMS)[number];

// Per-platform identifiers for the connected rows. The metaads/facebook/instagram
// fields come from the zernioProfiles CACHE (deriveDetails); the googleAds +
// googleBusiness fields come from the LIVE listAccounts response the resolve
// already makes (extractLiveConnectionDetails) — no extra Zernio call. Each is
// null when not resolvable.
export type ApiConnectionDetails = {
  metaadsScopedAdAccountIds: string[]; // act_… ids the profile is scoped to
  facebookPageId: string | null;
  facebookPageName: string | null;
  instagramId: string | null;
  instagramUsername: string | null;
  // Google Ads: the CURRENT bound customer id (the re-scoped one — see
  // resolveBoundGoogleAdsCustomerId) + its currency and account name, resolved
  // from the global Google Ads accounts cache by that id. Currency is the
  // correctness tell (NZD on an AU client = wrong account bound); null when the
  // cache can't resolve it (rendered as "—", never the stale metadata currency).
  googleAdsCustomerId: string | null;
  googleAdsCurrency: string | null;
  googleAdsAccountName: string | null;
  // Google Business: the bound location's human-readable display name (NOT the
  // resource name) + its bare location id.
  googleBusinessLocationName: string | null;
  googleBusinessLocationId: string | null;
};

// Ad-account drift: the account's fbAdAccountId no longer matches the profile's
// scope (both normalized to act_…). null = aligned / not applicable.
export type ApiConnectionDrift = {
  accountAdAccountId: string; // normalized act_… the Account targets
  scopedAdAccountIds: string[]; // normalized act_… the profile is scoped to
};

export type ApiConnections = {
  // The account's linked Zernio profile (null = no profile connected yet).
  profileId: string | null;
  profileName: string | null;
  // false = Zernio was unreachable — the UI shows "status unavailable" rather
  // than all-unticked (false negatives are worse than unknown).
  available: boolean;
  fetchedAt: IsoTimestamp | null;
  // null when there is no profile or Zernio was unreachable.
  platforms: Record<ApiConnectionPlatform, boolean> | null;
  // Per-platform OAuth token expiry, read straight off the SAME live listAccounts
  // rows as `platforms` (no extra Zernio call). An expiry WELL in the past means
  // the refresh has stopped — the token was probably revoked — even while every
  // other field reads connected. See src/lib/tokenHealth.ts for the threshold and
  // why this is a heuristic ("may need reconnecting", never "dead"). null per
  // platform = no expiry reported; the whole map is null when there is no profile
  // or Zernio was unreachable (mirrors `platforms`).
  tokenExpiry: Record<ApiConnectionPlatform, IsoTimestamp | null> | null;
  // Connected-row identifiers (null when no profile / cache miss).
  details: ApiConnectionDetails | null;
  // Set when the Account's ad account drifted from the profile's scope.
  adAccountDrift: ApiConnectionDrift | null;
  // Ghost-profile detection: false ONLY when profiles.getProfile returned a
  // DEFINITIVE 404 — the linked Zernio profile was deleted in Zernio, so
  // reconnect/re-onboard are wedged and the account needs a connection reset.
  // true when the profile exists (any connected platform → obviously exists, or
  // getProfile 200). null = unknown (no profile, Zernio unreachable, or not
  // checked) — NEVER claim ghost on a network error.
  profileExists: boolean | null;
};
